import { useEffect, useState } from 'react';
import { useCustomFieldsSchemaQuery } from '../../lib/Queries';
import TaggableInput from '../TaggableInput.jsx';
import Select from '../Select';
import CheckboxGroup from '../CheckboxGroup.jsx';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline/index.js';
import { getBusinessUrl } from '~/lib/useBusinessNavigate.js';
import { Link } from 'react-router-dom';

const generateUniqueId = () => {
    return 'field_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now().toString(36);
};

const CustomFieldSettings = ({ settings, setSettings, schema }) => {
    const [targetType, setTargetType] = useState(settings?.target_type || 'client');
    const [fieldAssignments, setFieldAssignments] = useState(settings?.field_assignments || [{ field_key: '', field_value: '', id: generateUniqueId() }]);

    const { data: customFieldsSchema, isLoading } = useCustomFieldsSchemaQuery(
        targetType === 'client' ? 'client' : 'bookingevent'
    );

    // Update settings when local state changes
    useEffect(() => {
        setSettings(prev => ({
            ...prev,
            target_type: targetType,
            field_assignments: fieldAssignments,
        }));
    }, [targetType, fieldAssignments]);

    // Update local state when settings change from outside
    useEffect(() => {
        if (settings?.target_type && settings.target_type !== targetType) {
            setTargetType(settings.target_type);
        }
        if (settings?.field_assignments && JSON.stringify(settings.field_assignments) !== JSON.stringify(fieldAssignments)) {
            setFieldAssignments(settings.field_assignments);
        }
    }, [settings]);

    const handleTargetTypeChange = (newType) => {
        setTargetType(newType);
        // Reset field assignments when changing target type
        setFieldAssignments([{ field_key: '', field_value: '', id: generateUniqueId() }]);
    };

    const handleFieldAssignmentChange = (index, field, value) => {
        const newAssignments = [...fieldAssignments];
        newAssignments[index] = {
            ...newAssignments[index],
            [field]: value,
        };
        setFieldAssignments(newAssignments);
    };

    const addFieldAssignment = () => {
        setFieldAssignments([...fieldAssignments, { field_key: '', field_value: '', id: generateUniqueId() }]);
    };

    const removeFieldAssignment = (index) => {
        if (fieldAssignments.length > 1) {
            const newAssignments = fieldAssignments.filter((_, i) => i !== index);
            setFieldAssignments(newAssignments);
        }
    };

    // Create field options for dropdown
    const fieldOptions = { '': 'Choose a field...' };
    if (customFieldsSchema && Array.isArray(customFieldsSchema)) {
        customFieldsSchema.forEach(field => {
            fieldOptions[field.key] = field.schema.label || field.key;
        });
    }

    const targetTypeOptions = [
        { label: 'Client', value: 'client' },
    ];

    // Only add event if we're working on an event schema
    if (schema.find(s => s.key === 'event-start')) {
        targetTypeOptions.push({ label: 'Event', value: 'event' });
    }

    if (isLoading) {
        return <div>Loading custom fields...</div>;
    }

    return (
        <div>
            {targetTypeOptions.length > 1 && (
                <>
                    <div className="input-label">Set Custom Fields On</div>
                    <CheckboxGroup
                        type="radio"
                        value={targetType}
                        onChange={handleTargetTypeChange}
                        options={targetTypeOptions}
                        className="!flex mb-4 !gap-4 [&>label]:!p-0"
                        hideSelectedBorder={true}
                    />
                </>
            )}


            <div className="flex [&>label]:items-center justify-between">
                <div className="input-label">Fields</div>
                <div className="text-xs text-gray-500 mr-4">
                    Custom Fields can be managed in <a href={getBusinessUrl('/settings/customfields')} className="text-gray-500" target="_blank">Settings › Fields</a>
                </div>
            </div>
            {fieldAssignments.map((assignment, index) => (
                <div key={assignment.id} className="border rounded-lg p-4 mb-4 bg-gray-100">
                    {fieldAssignments.length > 1 && (
                        <div className="float-end">
                            <button
                                type="button"
                                onClick={() => {
                                    if (confirm('Are you sure you want to remove this field assignment?')) {
                                        removeFieldAssignment(index);
                                    }
                                }}
                                className="text-red-600 hover:text-red-800"
                            >
                                <TrashIcon className="w-4 h-4" />
                            </button>
                        </div>
                    )}

                    <Select
                        label="Custom Field"
                        value={assignment.field_key}
                        onChange={(value) => handleFieldAssignmentChange(index, 'field_key', value)}
                        options={fieldOptions}
                        className="mb-3"
                    />

                    <div className="input-label">Field Value</div>
                        <div className="custom-field-settings">
                            <TaggableInput
                            schema={schema}
                            className="[&_.ProseMirror]:border-none [&_.ProseMirror]:outline-none"
                            initialValue={assignment.field_value}
                            onChange={(value) => handleFieldAssignmentChange(index, 'field_value', value)}
                            placeholder="Enter the value to set for this field..."
                            />
                        </div>
                </div>
            ))}

            <button
                type="button"
                onClick={addFieldAssignment}
                className="text-primary-700 text-sm flex items-center"
            >
                <PlusIcon className="w-4 h-4 mr-2" />
                Add Another Field
            </button>
        </div>
    );
};

export default CustomFieldSettings;
